import React, { useCallback, useEffect, useState } from 'react';
import { useCustomerStatus } from 'src/core/state/user';
import { useItemCount } from 'src/core/state/cart';
import { useSignoutCallback } from 'src/core/state/user';
import { of } from 'rxjs';
import { usePathnameObservable, useSignedInObservable } from 'src/hooks/router-hooks';
import { SearchLoader } from 'src/components/Algolia/SearchDropdown/SearchLoader';
import { useDeps } from 'src/hooks/useDeps';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Breakpoint, useBreakpoint, useRenderEnv } from 'src/hooks/useBreakpoint';
import classnames from 'classnames';
import { DataStatus } from 'src/core/engine/types';
import { Nav } from 'src/components/Nav/Nav';
import { useCspIsLoggedIn } from 'src/components/CustomerServicePortal/hooks/useCspLogin';
import { CartState } from 'src/core/state/cart';
import { CartItemType } from 'src/util/enums';
import { useHeaderColor } from 'src/components/Header/hooks/useHeaderColor';
import Bugsnag from '@bugsnag/js';
import { SubHeader } from 'src/components/Header/SubHeader';
import { HeaderCallUs } from 'src/components/Header/HeaderCallUs';
import { HeaderColor } from 'src/types/global-types';
import { useCreditBalance } from 'src/hooks/useCreditBalance';

import { Container, Wrapper } from '../Layout';

import classes from './Header.scss';
import { HeaderControls } from './HeaderControls';
import { HeaderLogo } from './HeaderLogo';

type HeaderProps = {
    isOpen: boolean;
    whiteIcons: boolean;
    backgroundColor?: HeaderColor;
};

export const HeaderContext = React.createContext({
    isSignedIn: false,
    showNav: false,
    showSearch: false,
    itemCount: 0,
    cartPending: false,
    pathname$: of(''),
    signedIn$: of(false),
    goToAccount: () => {
        Bugsnag.notify('goToAccount not implemented');
    },
    setShowNav: (..._args: any[]) => {
        Bugsnag.notify('unable to open Navigation');
    },
    setShowSearch: (..._args: any[]) => {
        Bugsnag.notify('unable to open Search');
    },
    logout: (..._args: any[]) => {
        Bugsnag.notify('unable to open logout');
    },
    isCsRSignedIn: false,
    creditAvailable: undefined as number | undefined,
    creditMax: undefined as number | undefined,
});

export const Header: React.FC<React.PropsWithChildren<HeaderProps>> = React.memo(
    ({ isOpen, children, whiteIcons, backgroundColor }) => {
        return (
            <>
                <SubHeader whiteIcons={whiteIcons} />
                <Wrapper
                    element="header"
                    bg={backgroundColor}
                    data-test-id="DefaultHeader"
                    className={classnames(classes.header, {
                        [classes.headerWithOverlay]: isOpen,
                    })}
                >
                    <Container className={classes.headerContainer}>
                        <div className={classes.headerLeft}>
                            <HeaderLogo />
                            <SearchLoader />
                            <HeaderCallUs whiteIcons={whiteIcons} />
                        </div>
                        <div className={classes.headerRight}>
                            <HeaderControls whiteIcons={whiteIcons} />
                        </div>
                    </Container>
                    {children}
                </Wrapper>
                {isOpen && <div className={classes.overlay}> </div>}
            </>
        );
    },
);

const stateSelector = (x: { cart: CartState }) =>
    x.cart.items.filter((item) => String(item.__typename) !== CartItemType.VirtualCartItem).length;

export const HeaderProvider: React.FC<{ children?: React.ReactNode }> = React.memo(({ children }) => {
    const itemCount: number = useSelector(stateSelector, shallowEqual);
    const [showNav, setShowNav] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const { isSignedIn: isSignedInState } = useCustomerStatus();
    const { isBrowser } = useRenderEnv();
    const isSignedIn = isSignedInState && isBrowser;
    const { status } = useItemCount();
    const pathname$ = usePathnameObservable();
    const signedIn$ = useSignedInObservable();
    const logout = useSignoutCallback();
    const { paths, replace } = useDeps();
    const dispatch = useDispatch();
    const goToAccount = useCallback(() => {
        dispatch(replace(paths.customer.account));
    }, [dispatch, paths, replace]);
    const [breakpoint] = useBreakpoint();
    const isCsRSignedIn = useCspIsLoggedIn();

    const { creditAvailable, creditMax } = useCreditBalance(!isSignedIn);

    const backgroundColor = useHeaderColor();
    const whiteIcons = backgroundColor !== 'orange';

    useEffect(() => {
        if (breakpoint === Breakpoint.Desk) return;
        if (!showSearch && !showNav) return;

        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [breakpoint, showNav, showSearch]);

    return (
        <HeaderContext.Provider
            value={{
                showNav: showNav,
                setShowNav,
                showSearch: showSearch,
                setShowSearch,
                isSignedIn,
                itemCount: itemCount || 0,
                cartPending: status === DataStatus.Pending,
                pathname$,
                signedIn$,
                logout,
                goToAccount,
                isCsRSignedIn: isCsRSignedIn || false,
                creditAvailable,
                creditMax,
            }}
        >
            <Header
                isOpen={breakpoint === Breakpoint.Desk ? false : showNav || showSearch}
                whiteIcons={whiteIcons}
                backgroundColor={backgroundColor}
            >
                <Nav whiteIcons={whiteIcons} />
            </Header>
            {children}
        </HeaderContext.Provider>
    );
});
